import { Parameters, ReplayLink } from '@/components/controls';
import {
  useAggregate,
  useOptions,
  usePeople,
  useTripClassifications,
  useUpdateTrips,
  useUserInfo,
} from '@/hooks';
import { isEmpty } from '@/utils';
import { events } from '@/utils/config';
import {
  Clear as ClearIcon,
  Label as LabelIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { differenceInDays } from 'date-fns';
import { useAtom } from 'jotai';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { Fragment, useMemo, useState } from 'react';
import { DownloadPollsLink } from './DownloadPollsLink';
import { TripDetail } from './TripDetail';
import {
  columnsFn,
  downloadPipelineFn,
  pipelineFn,
  stateAtom,
  totalsPipelineFn,
} from './utils';

const {
  eventFilters: { trips: simpleEventFilters },
} = events;

export function Trips() {
  const [{ sorting, pagination, rowSelection, query, parameters }, setState] =
    useAtom(stateAtom);
  const [editMode, setEditMode] = useState(null);
  const [classification, setClassification] = useState(null);
  const [driver, setDriver] = useState(null);
  const collection = 'trips';
  const {
    query: { data: vehicleCategories },
  } = useOptions('vehicleGroup');
  const pipeline = useMemo(
    () =>
      pipelineFn(
        pagination,
        query,
        sorting,
        vehicleCategories.map(({ value }) => value),
      ),
    [pagination, query, sorting, vehicleCategories],
  );
  const totalsPipeline = useMemo(() => totalsPipelineFn(query), [query]);
  const {
    query: { data, isFetching },
    cancel,
  } = useAggregate(collection, pipeline, !isEmpty(query), 1000 * 60 * 60);
  const {
    query: { data: aggregated, isFetching: totalsFetching },
    cancel: cancelTotals,
  } = useAggregate(collection, totalsPipeline, !isEmpty(query), 1000 * 60 * 60);
  const {
    query: { data: people },
  } = usePeople();
  const drivers = useMemo(
    () =>
      people
        .filter(
          (person) => !person.leavingDate || person.leavingDate > new Date(),
        )
        .map((person) => ({
          label: `${
            person.collarNumber
              ? `[${person.collarNumber}] `
              : `#${person.code} `
          }${person.forenames} ${person.surname}`,
          value: person.code,
          sort: [
            person.collarNumber ?? 'Ω',
            person.surname ?? 'Ω',
            person.forenames ?? 'Ω',
          ].join(''),
        }))
        .sort((a, b) => a.sort.localeCompare(b.sort)),
    [people],
  );
  const { mutate: updateTrips } = useUpdateTrips();
  const {
    query: { data: userInfo },
  } = useUserInfo();
  const canEdit = userInfo?.authorisation?.tripEvents?.write ?? false;
  const isAdmin = userInfo?.authorisation?.administrator ?? false;
  const loggedInPersonCode = userInfo?.person?.code ?? '';
  const canAssignOwnTrip =
    loggedInPersonCode !== '' &&
    (userInfo?.authorisation?.drivers?.assignOwnTrip ?? false);
  const {
    query: { data: tripClassifications },
  } = useTripClassifications();

  tripClassifications.forEach((classification) => {
    const availableTo = classification.availableTo ?? [];
    const userGroups = new Set(userInfo?.groups ?? []);
    classification.enabled =
      isAdmin || (!isAdmin && availableTo.some((item) => userGroups.has(item)));
  });
  const canEditClassifications =
    isAdmin || tripClassifications.some((item) => item.enabled);

  const eventFilters = useMemo(
    () => [
      {
        name: 'classification',
        label: 'Classification',
        type: 'select',
        multiple: true,
        options: tripClassifications,
      },
      ...simpleEventFilters,
    ],
    [tripClassifications],
  );
  const columns = useMemo(
    () =>
      columnsFn(
        aggregated[0] ?? {},
        canEdit,
        tripClassifications,
        vehicleCategories,
      ),
    [aggregated, canEdit, tripClassifications, vehicleCategories],
  );
  const csvColumns = columns
    .map(({ header, id, accessorKey }) => ({
      header,
      key: id ?? accessorKey,
    }))
    .concat({ header: 'Link', key: 'link' });

  function handleClearSelectionClick() {
    setState((state) => ({ ...state, rowSelection: {} }));
  }

  function handleEditModeChange(event, newMode) {
    setEditMode(newMode);
    setState((state) => ({ ...state, rowSelection: {} }));
    setDriver(null);
    setClassification(null);
  }

  function handleClassificationChange(event, newValue) {
    setClassification(newValue);
  }

  function handleDriverChange(event, newValue) {
    setDriver(newValue);
  }

  async function handleUpdateClick() {
    const ids = Object.entries(rowSelection)
      .filter(([, selected]) => selected)
      .map(([id]) => id);

    if (editMode === 'classification') {
      updateTrips({ ids, values: { classification: classification.value } });
    } else if (editMode === 'driver') {
      updateTrips({
        ids,
        values: { driverCode: isAdmin ? driver.value : loggedInPersonCode },
      });
    }
  }

  function renderTopToolbarCustomActions() {
    return canEdit ? (
      <Stack spacing={1} direction="row" sx={{ alignItems: 'center', flex: 1 }}>
        <ToggleButtonGroup
          value={editMode}
          onChange={handleEditModeChange}
          color="primary"
          size="small"
          exclusive
        >
          {canEditClassifications && (
            <Tooltip title="Set classification">
              <ToggleButton value="classification">
                <LabelIcon />
              </ToggleButton>
            </Tooltip>
          )}
          {(isAdmin || canAssignOwnTrip) && (
            <Tooltip title="Set driver">
              <ToggleButton value="driver">
                <PersonIcon />
              </ToggleButton>
            </Tooltip>
          )}
        </ToggleButtonGroup>
        {editMode !== null && (
          <Fragment>
            {editMode === 'classification' && (
              <Autocomplete
                size="small"
                value={classification}
                onChange={handleClassificationChange}
                options={tripClassifications}
                getOptionDisabled={(classification) => !classification.enabled}
                renderInput={(params) => (
                  <TextField placeholder="Select classification" {...params} />
                )}
                sx={{ minWidth: 240 }}
              />
            )}
            {editMode === 'driver' && isAdmin && (
              <Autocomplete
                size="small"
                value={driver}
                onChange={handleDriverChange}
                options={drivers}
                getOptionKey={(option) => option.value}
                renderInput={(params) => (
                  <TextField placeholder="Select driver" {...params} />
                )}
                sx={{ minWidth: 240 }}
              />
            )}
            <Button
              variant="contained"
              color="primary"
              disabled={Object.keys(rowSelection).length === 0}
              onClick={handleUpdateClick}
            >
              Update {Object.keys(rowSelection).length} trips
            </Button>
            {Object.keys(rowSelection).length > 0 && (
              <Tooltip title="Clear selection">
                <IconButton
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={handleClearSelectionClick}
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            )}
          </Fragment>
        )}
      </Stack>
    ) : undefined;
  }

  function renderRowActions(cell) {
    return (
      <Stack spacing={0.5} direction="row">
        <ReplayLink
          collection={collection}
          identifier={cell.row.original.identifier}
        />
        <DownloadPollsLink entry={cell.row.original} />
      </Stack>
    );
  }

  function renderDetailPanel(cell) {
    return <TripDetail trip={cell.row.original} />;
  }

  function handleCancel() {
    cancel();
    cancelTotals();
    setState((state) => ({ ...state, query: {} }));
  }

  const handleStateChange = (name) => (value) => {
    setState((state) => ({ ...state, [name]: value }));
  };

  const handleStateChangeWithEvent = (name) => (_, value) => {
    setState((state) => ({ ...state, [name]: value }));
  };

  const handleTableStateChange = (name) => (changeFn) => {
    setState((state) => ({ ...state, [name]: changeFn(state[name]) }));
  };

  const handleFieldBlur = (cell) => (event) => {
    updateTrips({
      id: cell.row.original.identifier,
      values: { [event.target.name]: event.target.value },
    });
  };

  function enableRowSelection(row) {
    if (editMode === 'classification') {
      return true;
    }

    if (editMode === 'driver') {
      return (
        !row.original.driver?.code &&
        differenceInDays(
          new Date(),
          row.original.startTime ?? new Date('2000-01-01'),
        ) < 30
      );
    }

    return false;
  }

  const table = useMaterialReactTable({
    columns,
    data,
    state: {
      density: 'compact',
      sorting,
      pagination,
      rowSelection,
      isLoading: isFetching,
    },
    defaultColumn: { size: 0, enableEditing: false },
    getRowId: (row) => row.identifier,
    rowCount: aggregated[0]?.total ?? 0,
    onPaginationChange: handleTableStateChange('pagination'),
    onSortingChange: handleTableStateChange('sorting'),
    onRowSelectionChange: handleTableStateChange('rowSelection'),
    renderTopToolbarCustomActions,
    renderRowActions,
    renderDetailPanel,
    enableExpandAll: false,
    positionToolbarAlertBanner: 'none',
    positionActionsColumn: 'last',
    editDisplayMode: 'cell',
    enableEditing: canEdit,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableTopToolbar: true,
    enableSorting: true,
    enableBottomToolbar: true,
    enablePagination: true,
    enableRowSelection,
    enableRowActions: true,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    muiEditTextFieldProps: ({ cell }) => ({
      variant: 'outlined',
      size: 'small',
      slotProps: { htmlInput: { sx: { fontSize: 14 } } },
      onBlur: handleFieldBlur(cell),
    }),
    muiTablePaperProps: { elevation: 0, variant: 'elevation' },
    muiTableContainerProps: {
      sx: {
        width: 'calc(100vw - 280px)',
        maxHeight: 'calc(100vh - 160px)', // 48 app header + 56 top bar + 56 bottom bar
      },
    },
  });

  return (
    <Stack direction="row">
      <title>IR3 | Trips</title>
      <Box
        sx={{
          width: 280,
          height: 'calc(100vh - 48px)',
          borderRight: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Parameters
          collection={collection}
          onFetch={handleStateChangeWithEvent('query')}
          onCancel={handleCancel}
          isFetching={isFetching || totalsFetching}
          value={parameters}
          onChange={handleStateChange('parameters')}
          sx={{ pt: 1, height: 1 }}
          vehicle
          driver
          rfidCard
          eventFilters={eventFilters}
          pipelineFn={downloadPipelineFn(
            collection,
            vehicleCategories.map(({ value }) => value),
          )}
          columns={csvColumns}
        />
      </Box>
      <MaterialReactTable table={table} />
    </Stack>
  );
}
